
import { useEffect, useMemo, useState } from "react";
import { ref as storageRef, uploadBytes, getDownloadURL } from "firebase/storage";
import { updateProfile } from "firebase/auth";
import { auth, storage } from "@/lib/firebase";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Upload, X } from "lucide-react";
import { ROLE_SCHEMAS, RoleKey, FieldDef, getPath, setPath } from "./roleProfileSchema";
import { useRoleProfile } from "./useRoleProfile";
import { ProfilePreferences } from "./ProfilePreferences";
import { ProfileVisibility } from "./ProfileVisibility";
import { ProfileDocuments } from "./ProfileDocuments";

function TagsField({ label, value, onChange, disabled, lang }: { label: string; value: any; onChange: (v: any) => void; disabled?: boolean; lang: "en" | "fr" }) {
  const arr: string[] = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState("");
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (arr.includes(v)) { setDraft(""); return; }
    onChange([...arr, v]);
    setDraft("");
  };
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-2">
        <Input value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder={lang === "fr" ? "Ajouter et appuyer sur Entrée" : "Type and press Enter"} disabled={disabled} />
        <Button type="button" variant="outline" onClick={add} disabled={disabled}>+</Button>
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {arr.map((t) => (
          <Badge key={t} variant="secondary" className="gap-1">
            {t}
            {!disabled && (
              <button type="button" onClick={() => onChange(arr.filter((x) => x !== t))}>
                <X className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
    </div>
  );
}

function FieldRenderer({ field, value, onChange, lang }: { field: FieldDef; value: any; onChange: (v: any) => void; lang: "en" | "fr" }) {
  const label = field.label[lang];
  if (field.type === "textarea") {
    return (
      <div>
        <Label>{label}</Label>
        <Textarea rows={field.rows || 3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} disabled={field.readonly} />
      </div>
    );
  }
  if (field.type === "tags") {
    return <TagsField label={label} value={value} onChange={onChange} disabled={field.readonly} lang={lang} />;
  }
  return (
    <div>
      <Label>{label}</Label>
      <Input
        type={field.type === "number" ? "number" : field.type === "email" ? "email" : field.type === "tel" ? "tel" : "text"}
        value={value ?? ""}
        onChange={(e) => onChange(field.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
        disabled={field.readonly}
      />
    </div>
  );
}

export default function RoleProfileEditor({ role }: { role: RoleKey }) {
  const schema = ROLE_SCHEMAS[role];
  const { user } = useAuth();
  const { language } = useLanguage();
  const lang = (language as "en" | "fr") || "en";
  const { toast } = useToast();
  const { data, loading, saving, save } = useRoleProfile(role);
  const [draft, setDraft] = useState<any>({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!loading) setDraft(data || {});
  }, [loading, data]);

  const t = useMemo(() => (lang === "fr"
    ? { profile: "Profil", preferences: "Préférences", visibility: "Visibilité", documents: "Documents", photo: "Photo", upload: "Téléverser", save: "Enregistrer", saving: "Enregistrement…", saved: "Profil mis à jour", error: "Erreur lors de l'enregistrement", loading: "Chargement…" }
    : { profile: "Profile", preferences: "Preferences", visibility: "Visibility", documents: "Documents", photo: "Photo", upload: "Upload", save: "Save", saving: "Saving…", saved: "Profile updated", error: "Could not save profile", loading: "Loading…" }), [lang]);

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
  }

  const photoURL = draft.photoURL || user?.photoURL || "";
  const initials = `${(draft.firstName?.[0] || "")}${(draft.lastName?.[0] || "")}`.toUpperCase() || (user?.email?.[0]?.toUpperCase() || "U");

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user?.id) return;
    setUploading(true);
    try {
      const ext = file.name.split(".").pop() || "jpg";
      const path = `avatars/${user.id}/avatar.${ext}`;
      const r = storageRef(storage, path);
      await uploadBytes(r, file);
      const url = await getDownloadURL(r);
      setDraft((d: any) => ({ ...d, photoURL: url }));
      await save({ photoURL: url });
      if (auth.currentUser) await updateProfile(auth.currentUser, { photoURL: url });
    } catch (err) {
      console.error("[RoleProfileEditor] photo upload", err);
      toast({ title: t.error, description: String((err as Error)?.message || ""), variant: "destructive" });
    } finally {
      setUploading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      const displayName = `${draft.firstName || ""} ${draft.lastName || ""}`.trim() || draft.displayName || user?.displayName || undefined;
      const updates: any = {};
      schema.sections.forEach((s) =>
        s.fields.forEach((f) => {
          if (f.readonly) return;
          const v = getPath(draft, f.key);
          if (v !== undefined) {
            // Only set top-level keys explicitly; for nested paths, build the partial update.
            const top = f.key.split(".")[0];
            if (!updates[top]) updates[top] = getPath(draft, top);
            setPath(updates, f.key, v);
          }
        })
      );
      if (displayName) updates.displayName = displayName;
      await save(updates);
      if (auth.currentUser && displayName) await updateProfile(auth.currentUser, { displayName });
      toast({ title: t.saved });
    } catch (err) {
      console.error("[RoleProfileEditor] save", err);
      toast({ title: t.error, description: String((err as Error)?.message || ""), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader><CardTitle>{schema.title[lang]}</CardTitle></CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Avatar className="h-20 w-20">
              <AvatarImage src={photoURL} alt={initials} />
              <AvatarFallback>{initials}</AvatarFallback>
            </Avatar>
            <div>
              <Label className="block mb-2">{t.photo}</Label>
              <label className="inline-flex">
                <input type="file" accept="image/*" className="hidden" onChange={handlePhoto} />
                <Button asChild variant="outline" size="sm" disabled={uploading}>
                  <span>
                    {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
                    {t.upload}
                  </span>
                </Button>
              </label>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="profile">
        <TabsList>
          <TabsTrigger value="profile">{t.profile}</TabsTrigger>
          <TabsTrigger value="preferences">{t.preferences}</TabsTrigger>
          <TabsTrigger value="visibility">{t.visibility}</TabsTrigger>
          <TabsTrigger value="documents">{t.documents}</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="space-y-4">
          {schema.sections.map((s) => (
            <Card key={s.id}>
              <CardHeader><CardTitle className="text-base">{s.title[lang]}</CardTitle></CardHeader>
              <CardContent className="grid md:grid-cols-2 gap-4">
                {s.fields.map((f) => {
                  const value = f.key === "email" ? (user?.email || "") : getPath(draft, f.key);
                  const onChange = (v: any) => setDraft((d: any) => setPath(d || {}, f.key, v));
                  return (
                    <div key={f.key} className={f.type === "textarea" || f.type === "tags" ? "md:col-span-2" : ""}>
                      <FieldRenderer field={f} value={value} onChange={onChange} lang={lang} />
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          ))}
          <div className="flex justify-end">
            <Button onClick={handleSaveProfile} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {saving ? t.saving : t.save}
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="preferences">
          <ProfilePreferences
            initial={draft.preferences}
            saving={saving}
            onSave={async (prefs) => { await save({ preferences: prefs }); setDraft((d: any) => ({ ...d, preferences: prefs })); }}
          />
        </TabsContent>

        <TabsContent value="documents">
          <ProfileDocuments
            documents={draft.documents}
            saving={saving}
            onSave={async (documents) => { await save({ documents }); setDraft((d: any) => ({ ...d, documents })); }}
          />
        </TabsContent>
        <TabsContent value="visibility">
          <ProfileVisibility
            schema={schema}
            initial={draft.visibility}
            saving={saving}
            onSave={async (visibility) => { await save({ visibility }); setDraft((d: any) => ({ ...d, visibility })); }}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
